// "Your reform" modal row selector (#65).
//
// Turns the share registry (state/share_store) into the modal's display list:
// one row per *changed* control, with composite kinds (bracket / categorical)
// expanded to one row per changed element. Each row carries a human label, the
// current value, and the delta from baseline — all derived purely from the
// store (value + the static `meta` each island registers), so the modal never
// reads the DOM. Pure and unit-tested in reform_summary.test.ts.
//
// Dispatcher sibling (see CLAUDE.md): a new `group$kind` must add a branch here
// AND have its island register a matching `ControlMeta` (state/share_store).

import { formatEur, formatPercent } from "../lib/formatters";
import type { ControlMeta, ControlRecord } from "./share_store";

export interface ReformRow {
  /** Stable per-row key (inputId, or inputId·sub for composite kinds). */
  key: string;
  /** Parent instrument's display title; the modal groups rows under it (#65). */
  instrument: string;
  /** Short, instrument-relative label for the row (e.g. "Bracket 4 rate"). */
  label: string;
  /** The control's current value, formatted for display. */
  valueText: string;
  /** The signed change from baseline, when distinct from the value (absent for
   *  delta sliders, whose value *is* the change). */
  deltaText?: string;
}

const MINUS = "−"; // U+2212, matches the rest of the UI's typography.

/** A percentage from a fraction, trimmed like the slider readouts ("21%"). */
function pctFromFraction(frac: number): string {
  return formatPercent(frac).replace(/\.0%$/, "%");
}

function signedEur(delta: number): string {
  return `${delta < 0 ? MINUS : "+"}${formatEur(Math.abs(delta))}`;
}

/** Signed percentage points, trimmed ("+5 pts", "−2 pts"). */
function signedPts(pts: number): string {
  const rounded = Math.round(pts * 10) / 10;
  return `${rounded < 0 ? MINUS : "+"}${Math.abs(rounded)} pts`;
}

function signedCount(delta: number): string {
  return `${delta < 0 ? MINUS : "+"}${Math.abs(Math.round(delta))}`;
}

type NumericFormat = "euro" | "percent" | "number";

function formatLevel(value: number, format: NumericFormat): string {
  if (format === "euro") return formatEur(value);
  if (format === "percent") return pctFromFraction(value);
  return String(Math.round(value));
}

function formatLevelDelta(delta: number, format: NumericFormat): string {
  if (format === "euro") return signedEur(delta);
  if (format === "percent") return signedPts(delta * 100);
  return signedCount(delta);
}

function isBracketValue(
  v: unknown,
): v is { boundaries: number[]; rates: number[] } {
  return (
    typeof v === "object" &&
    v !== null &&
    Array.isArray((v as { boundaries?: unknown }).boundaries) &&
    Array.isArray((v as { rates?: unknown }).rates)
  );
}

function isCommit(v: unknown): v is Record<string, string> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** The display rate of a categorical band ("12%", or "Exempt (0%)"). */
function bandText(
  bandId: string,
  bands: Array<{ id: string; rate: number }>,
): string {
  if (bandId === "exempt") return "Exempt (0%)";
  const band = bands.find((b) => b.id === bandId);
  return band ? pctFromFraction(band.rate) : bandId;
}

function rowsForControl(
  inputId: string,
  value: unknown,
  meta: ControlMeta,
): ReformRow[] {
  const instrument = meta.instrument;
  switch (meta.kind) {
    case "numeric": {
      const v = value as number;
      return [
        {
          key: inputId,
          instrument,
          label: meta.label,
          valueText: formatLevel(v, meta.format),
          deltaText: formatLevelDelta(v - meta.default, meta.format),
        },
      ];
    }
    case "delta": {
      const v = value as number;
      // The value already expresses the change vs baseline (default is 0), so
      // there's no separate delta to show.
      return [
        {
          key: inputId,
          instrument,
          label: meta.label,
          valueText: `${v > 0 ? "+" : ""}${v}${meta.postfix}`,
        },
      ];
    }
    case "bracket": {
      if (!isBracketValue(value)) break;
      // Short, instrument-relative labels — the instrument heading carries the
      // context, so the row drops the long group label (#65 review).
      const out: ReformRow[] = [];
      value.rates.forEach((rate, i) => {
        if (rate === meta.rateDefaults[i]) return;
        out.push({
          key: `${inputId}·rate·${i}`,
          instrument,
          label: `Bracket ${i + 1} rate`,
          valueText: pctFromFraction(rate),
          deltaText: signedPts((rate - meta.rateDefaults[i]) * 100),
        });
      });
      value.boundaries.forEach((boundary, i) => {
        if (boundary === meta.boundaryDefaults[i]) return;
        out.push({
          key: `${inputId}·boundary·${i}`,
          instrument,
          label: `Bracket ${i + 1} upper bound`,
          valueText: formatEur(boundary),
          deltaText: signedEur(boundary - meta.boundaryDefaults[i]),
        });
      });
      return out;
    }
    case "categorical": {
      if (!isCommit(value)) break;
      const out: ReformRow[] = [];
      for (const [categoryId, bandId] of Object.entries(value)) {
        const category = meta.categories.find((c) => c.id === categoryId);
        if (!category) continue;
        out.push({
          key: `${inputId}·${categoryId}`,
          instrument,
          label: category.label,
          valueText: bandText(bandId, meta.bands),
          deltaText: `was ${bandText(category.defaultBand, meta.bands)}`,
        });
      }
      return out;
    }
  }
  // Malformed value for the declared kind — degrade to a bare row rather than
  // dropping the change silently.
  return [{ key: inputId, instrument, label: meta.label, valueText: "modified" }];
}

/** The changed-controls row list for the "Your reform" modal. */
export function selectReformRows(
  controls: Record<string, ControlRecord>,
): ReformRow[] {
  const rows: ReformRow[] = [];
  for (const [inputId, rec] of Object.entries(controls)) {
    if (!rec.isModified) continue;
    if (!rec.meta) {
      // A modified control whose island hasn't registered meta — surface it
      // rather than silently omitting it (the dispatcher-sweep failure mode).
      rows.push({ key: inputId, instrument: "", label: inputId, valueText: "modified" });
      continue;
    }
    rows.push(...rowsForControl(inputId, rec.value, rec.meta));
  }
  return rows;
}

export interface ReformGroup {
  instrument: string;
  rows: ReformRow[];
}

/**
 * Group the flat reform rows by instrument, preserving first-appearance order
 * (both of the instruments and of the rows within each). The modal renders one
 * section per group with the instrument as its heading.
 */
export function groupReformRows(rows: ReformRow[]): ReformGroup[] {
  const groups: ReformGroup[] = [];
  const byInstrument = new Map<string, ReformGroup>();
  for (const row of rows) {
    let group = byInstrument.get(row.instrument);
    if (!group) {
      group = { instrument: row.instrument, rows: [] };
      byInstrument.set(row.instrument, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}
